import type { Transaction } from '../api/types'
import { splitRoundTripCycles, underlyingOf } from './activeTrades'

export interface EnrichedTransaction extends Transaction {
  pl: number | null
}

export interface DailySymbolPL {
  symbol: string
  /** Realized PL for this symbol on this day. 0 when no realizing SELLs happened (e.g. opens only). */
  pl: number
}

export interface DailyPL {
  date: string
  pl: number
  tradeCount: number
  /** Distinct underlying tickers traded that day, sorted by absolute PL desc. */
  symbols: DailySymbolPL[]
}

/**
 * Realized P/L using FIFO-style average cost basis. Uses netAmount
 * (cash-true) — the actual dollars that moved into or out of the account,
 * so fees and rebates are accounted for. Per-platform shape differences
 * are normalized away by the backend adapter, so this function reads only
 * canonical fields.
 */
export function enrichWithPL(transactions: Transaction[]): EnrichedTransaction[] {
  const sorted = [...transactions].sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  const book = new Map<string, { qty: number; cost: number }>()
  const out: EnrichedTransaction[] = []
  for (const t of sorted) {
    if (
      t.kind !== 'TRADE' ||
      !t.symbol ||
      !t.side ||
      t.quantity == null ||
      t.quantity <= 0 ||
      !Number.isFinite(t.netAmount)
    ) {
      out.push({ ...t, pl: null })
      continue
    }
    const qty = t.quantity
    const cashMagnitude = Math.abs(t.netAmount)
    const pos = book.get(t.symbol) ?? { qty: 0, cost: 0 }
    if (t.side === 'BUY') {
      pos.qty += qty
      pos.cost += cashMagnitude
      book.set(t.symbol, pos)
      out.push({ ...t, pl: null })
    } else {
      let pl: number | null = null
      if (pos.qty > 0) {
        const sellQty = Math.min(qty, pos.qty)
        const avgCost = pos.cost / pos.qty
        const costOfSold = avgCost * sellQty
        const proceedsForSold = cashMagnitude * (sellQty / qty)
        pl = proceedsForSold - costOfSold
        pos.qty -= sellQty
        pos.cost -= costOfSold
        book.set(t.symbol, pos)
      }
      out.push({ ...t, pl })
    }
  }
  return out
}

export function localDateKey(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export interface AggregateDailyPLOptions {
  /**
   * If provided, restrict aggregation to transactions whose `symbol` is in this
   * set. Used to scope counts to the symbols this app has placed orders on.
   * Filtering happens before FIFO so the per-symbol cost basis stays consistent
   * within the filtered scope.
   */
  symbolFilter?: Set<string>
}

/**
 * Aggregate transactions into daily totals using **round-trip cycles** as the
 * unit of a "trade". A cycle is a sequence of fills for a symbol from net-qty-
 * of-zero back to net-qty-of-zero (see `splitRoundTripCycles`). Each cycle
 * counts as one trade, booked on the local-time date of its closing fill, with
 * realized P/L equal to the sum of `netAmount` across the cycle.
 *
 * This matches the closed-position grouping rendered on the /trades page, so
 * day-strip counters, calendar counters, and the closed-cards list all agree.
 */
export function aggregateDailyPL(
  transactions: Transaction[],
  options: AggregateDailyPLOptions = {},
): Map<string, DailyPL> {
  const { symbolFilter } = options
  const scoped = symbolFilter
    ? transactions.filter((t) => t.symbol != null && symbolFilter.has(t.symbol))
    : transactions
  const map = new Map<string, DailyPL>()
  // symbolPL[dayKey][underlying] = realized PL booked on cycles closing this day
  const symbolPL = new Map<string, Map<string, number>>()
  // Track every symbol touched (even pure-BUY / still-open days) so the chip
  // still appears in the calendar cell.
  for (const t of scoped) {
    if (t.kind !== 'TRADE' || !t.symbol) continue
    const key = localDateKey(t.timestamp)
    if (!key) continue
    const inner = symbolPL.get(key) ?? new Map<string, number>()
    const underlying = underlyingOf(t.symbol)
    if (!inner.has(underlying)) inner.set(underlying, 0)
    symbolPL.set(key, inner)
  }

  // Group all TRADE rows by symbol so cycles can span multiple days.
  const bySymbol = new Map<string, Transaction[]>()
  for (const t of scoped) {
    if (t.kind !== 'TRADE' || !t.symbol) continue
    const arr = bySymbol.get(t.symbol) ?? []
    arr.push(t)
    bySymbol.set(t.symbol, arr)
  }

  for (const [symbol, txs] of bySymbol) {
    const sorted = [...txs].sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    const cycles = splitRoundTripCycles(sorted)
    const underlying = underlyingOf(symbol)
    for (const cycle of cycles) {
      if (cycle.length === 0) continue
      const closeTs = cycle[cycle.length - 1].timestamp
      const dayKey = localDateKey(closeTs)
      if (!dayKey) continue
      const realized = cycle.reduce((sum, t) => sum + (t.netAmount ?? 0), 0)
      const cur = map.get(dayKey) ?? { date: dayKey, pl: 0, tradeCount: 0, symbols: [] }
      cur.pl += realized
      cur.tradeCount += 1
      map.set(dayKey, cur)
      const inner = symbolPL.get(dayKey) ?? new Map<string, number>()
      inner.set(underlying, (inner.get(underlying) ?? 0) + realized)
      symbolPL.set(dayKey, inner)
    }
  }

  for (const [key, inner] of symbolPL) {
    const cur = map.get(key)
    if (!cur) continue
    cur.symbols = [...inner.entries()]
      .map(([symbol, pl]) => ({ symbol, pl }))
      .sort((a, b) => Math.abs(b.pl) - Math.abs(a.pl) || a.symbol.localeCompare(b.symbol))
  }
  return map
}
